import { BarChart2 } from 'react-feather';
import { ChangeEvent } from 'react';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import {
  ExportFormat,
  ExportTeamsMutation as ExportTeams,
  ParticipantFilters,
} from '__generated__/graphql';

import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import ExportTeamsMutation from './ExportTeamsMutation';
import GetExportProfilesQuery from '../../Exports/GetExportProfilesQuery';
import GetExportsQuery from '../../Exports/GetExportsQuery';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

export interface ExportTeamsFormProps {
  filters: ParticipantFilters;
  teamsCount: number;
  onSuccess?: (exported: ExportTeams['exportTeams']) => void;
  refetchQueries?: DocumentNode[];
}

const ExportTeamsForm = ({
  filters, teamsCount, onSuccess,
  refetchQueries = [GetExportsQuery],
}: ExportTeamsFormProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const project = useProject();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      export_profile_type: 'none',
      export_profile: null,
      columns: [],
      options: {
        format: ExportFormat.excel,
      },
    },
  });

  const handleExportProfileTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;

    if (value === 'none') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'none',
        export_profile: null,
      }));
    } else if (value === 'create_new') {
      form.set((values) => ({
        ...values,
        export_profile_type: 'create_new',
        export_profile: {
          title: '',
        },
      }));
    } else if (value) {
      form.set((values) => ({
        ...values,
        export_profile_type: value,
        export_profile: {
          id: value,
        },
      }));
    }
  };

  const enabledColumns = form.values.columns.reduce(
    (values, { column, title, enabled }) => (enabled ? [...values, { column, title }] : values),
    [],
  );

  const [exportRegistrations, { loading, error }] = useMutation(
    ExportTeamsMutation,
    {
      variables: {
        input: {
          project: project.id,
          export_profile: form.values.export_profile,
          columns: enabledColumns,
          filters,
          options: {
            ...form.values.options,
          },
        },
      },
      onCompleted: ({ exportTeams }) => {
        notifier.success(t('notifications.export_started'));
        onSuccess?.(exportTeams);
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const eventId = (filters.events || []).length === 1 ? filters.events[0] : null;

  const {
    data: exportProfilesData,
  } = useQuery(GetExportProfilesQuery, {
    variables: {
      project_id: project.id,
      type: 'teams',
      event_id: eventId,
    },
    onCompleted: (data) => {
      form.reset((values) => ({
        ...values,
        columns: data.project.default_export_columns.map((column) => ({
          column: column.id,
          title: '',
          enabled: false,
        })),
      }));
    },
  });

  const selectedExportProfile = exportProfilesData?.project.export_profiles.filter(
    (exportProfile) => exportProfile.id === form.values.export_profile?.id,
  )[0];

  const handleSelectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: true })),
    }));
  };

  const handleDeselectAllColumns = () => {
    form.set((values) => ({
      ...values,
      columns: form.values.columns.map((value) => ({ ...value, enabled: false })),
    }));
  };

  const allColumnsSelected = form.values.columns.filter((value) => !value.enabled).length === 0;
  const noColumnsSelected = form.values.columns.filter((value) => value.enabled).length === 0;

  if (!exportProfilesData) {
    return <UI.PageLoader />;
  }

  return (
    <UI.Form onSubmit={() => exportRegistrations()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('export_teams')}
          </UI.H4>
          <UI.Paragraph sc={{ mb: 0, mt: 2 }}>
            {t('export_teams_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.HR />

        <UI.ServerErrors error={error} />

        <UI.FormGrid>
          <UI.InputGroup>
            <UI.InputLabel htmlFor="export_profile_type">
              {t('export_profile')}
            </UI.InputLabel>
            <UI.Select
              value={form.values.export_profile_type}
              onChange={handleExportProfileTypeChange}
              id="export_profile_type"
            >
              <option value="none">
                {t('all_columns')}
              </option>
              <option value="create_new">
                {t('custom_columns')}
              </option>
              {exportProfilesData?.project.export_profiles.map((exportProfile) => (
                <option value={exportProfile.id} key={exportProfile.id}>
                  {exportProfile.title}
                  {' • '}
                  {t('n_columns', {
                    value: formatNumber(exportProfile.column_titles.length),
                    count: exportProfile.column_titles.length,
                  })}
                  {' • '}
                  {formatDate(parseDate(exportProfile.created_at), { format: 'display_compact_date' })}
                </option>
              ))}
            </UI.Select>
          </UI.InputGroup>

          {form.values.export_profile_type === 'create_new' && (
            <UI.InputGroup>
              <UI.InputLabel>
                {t('columns')}
                <UI.Span sc={{ muted: true }}> • </UI.Span>
                <UI.A
                  onClick={() => handleSelectAllColumns()}
                  sc={{ disabled: allColumnsSelected }}
                >
                  {t('select_all')}
                </UI.A>
                <UI.Span sc={{ muted: true }}> • </UI.Span>
                <UI.A
                  onClick={() => handleDeselectAllColumns()}
                  sc={{ disabled: noColumnsSelected }}
                >
                  {t('deselect_all')}
                </UI.A>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('create_export_profile_description')}
              </UI.InputDescription>
              <UI.FormGrid sc={{ gutter: 0.0625 }}>
                {form.values.columns.map((value, index) => {
                  const column = exportProfilesData?.project.default_export_columns[index];

                  return (
                    <UI.FormGrid
                      sc={{
                        columns: '1fr 1fr',
                        gutter: 0.25,
                        background: 'gray.75',
                      }}
                      key={value.column}
                    >
                      <UI.Div style={{ minWidth: 0 }}>
                        <UI.Checkbox
                          {...form.checkbox(`columns.${index}.enabled`)}
                          sc={{
                            fontWeight: form.values.columns[index].enabled ? 500 : undefined,
                            pl: 6,
                            py: 2.5,
                            checkboxLeft: 12,
                            checkboxTop: 19,
                          }}
                        >
                          <UI.Truncate>
                            {column.title}
                          </UI.Truncate>
                        </UI.Checkbox>
                      </UI.Div>
                      <UI.Div sc={{ py: 1, pr: 2 }}>
                        <UI.Input
                          {...form.text(`columns.${index}.title`, { onChange: () => form.set(`columns.${index}.enabled`, true) })}
                          sc={{ size: 'md' }}
                          placeholder={column.title}
                        />
                      </UI.Div>
                    </UI.FormGrid>
                  );
                })}
              </UI.FormGrid>
            </UI.InputGroup>
          )}

          {selectedExportProfile && (
            <UI.Info>
              <UI.Delimit>
                {selectedExportProfile.column_titles}
              </UI.Delimit>
            </UI.Info>
          )}

          {form.values.export_profile_type === 'create_new' && (
            <UI.InputGroup>
              <UI.InputLabel htmlFor="add_export_profile_title">
                {t('export_profile_title')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('export_profile_title_description')}
              </UI.InputDescription>
              <UI.Input {...form.text('export_profile.title')} id="add_export_profile_title" />
            </UI.InputGroup>
          )}

          <UI.InputGroup>
            <UI.InputLabel htmlFor="add_export_format">
              {t('file_format')}
            </UI.InputLabel>
            <UI.Radio {...form.radio('options.format', ExportFormat.excel)} id="add_export_format">
              {t('export_formats.excel')}
            </UI.Radio>
            <UI.Radio {...form.radio('options.format', ExportFormat.csv)}>
              {t('export_formats.csv')}
            </UI.Radio>
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FlexContainer sc={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <UI.Div>
            <UI.Icon>
              <BarChart2 />
            </UI.Icon>
            {' '}
            {t('teams')}
            {': '}
            {formatNumber(teamsCount)}
          </UI.Div>
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={loading
              || (form.values.export_profile_type === 'create_new' && enabledColumns.length === 0)}
          >
            {t('export')}
          </UI.Button>
        </UI.FlexContainer>
      </UI.FormGrid>
    </UI.Form>
  );
};

export default ExportTeamsForm;
